import type { FetchResult, Reference, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';

import {
  useDeleteAlertSettingMutation,
  useUpdateAlertSettingMutation,
  type DeleteAlertSettingMutation,
  type DeleteAlertSettingMutationVariables,
  type UpdateAlertSettingMutation,
  type UpdateAlertSettingMutationVariables,
} from 'generated/alert_setting.generated';

export function useUpsertAlertSetting(): [
  (
    params: UpdateAlertSettingMutationVariables['input']
  ) => Promise<FetchResult<UpdateAlertSettingMutation>>,
  boolean,
] {
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateAlertSetting',
  });
  const [mutate, { loading: baseLoading }] = useUpdateAlertSettingMutation();
  const loading = baseLoading;

  async function upsertAlertSetting(
    params: UpdateAlertSettingMutationVariables['input']
  ) {
    const response = await mutate({
      variables: { input: { ...params } },
      update: (cache, { data: response }) => {
        if (!response?.updateAlertSetting) {
          return;
        }
        if (!params.alert_setting_id) {
          const storeObject =
            response.updateAlertSetting as unknown as StoreObject;
          try {
            cache.modify<{ listAlertSettings: Reference[] }>({
              fields: {
                listAlertSettings: (existing, { toReference }) => {
                  const ref = toReference(storeObject);
                  return ref ? [ref, ...existing] : existing;
                },
              },
            });
          } catch (e) {
            if (isDevelopment) {
              // eslint-disable-next-line no-console
              console.error(e);
            }
          }
        }
      },
    });
    const { errors } = response;
    if (errors) {
      handleError(errors);
    }
    return response;
  }
  return [upsertAlertSetting, loading];
}

export function useDeleteAlertSetting(): [
  (
    params: DeleteAlertSettingMutationVariables['input']
  ) => Promise<FetchResult<DeleteAlertSettingMutation>>,
  boolean,
] {
  const { handleError } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'deleteAlertSetting',
  });
  const [mutate, { loading: baseLoading }] = useDeleteAlertSettingMutation();
  const loading = baseLoading;

  async function deleteAlertSetting(
    params: DeleteAlertSettingMutationVariables['input']
  ) {
    const response = await mutate({
      variables: { input: { ...params } },
      update: (cache, { data: response }) => {
        if (!response?.deleteAlertSetting) {
          return;
        }
        try {
          const normalizedId = cache.identify({
            alert_setting_id: params.alert_setting_id,
            vitall_patient_id: params.vitall_patient_id,
            __typename: 'AlertSettingModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const { errors } = response;
    if (errors) {
      handleError(errors);
    }
    return response;
  }
  return [deleteAlertSetting, loading];
}
